import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/state/store/auth';
import { Input } from '@/components/ui-kit/input';
import { Button } from '@/components/ui-kit/button';
import { getApiKeyRoles, ROLES_PAGE_SIZE } from './api-key-roles.service';
import { getRoleRuleDefaults } from './api-key-scopes';
import { listApiKeyScopeRules, type ApiKeyScopeRule } from './api-key-scope-rules.service';
import {
  groupApiKeyScopeRules,
  resolveApiKeyScopeGrant,
  type ApiKeyScopeGrant,
} from './api-key-scope-resolver';

export function ApiKeyScopePicker({
  selection,
  onChange,
}: {
  selection: ApiKeyScopeGrant;
  onChange: (selection: ApiKeyScopeGrant) => void;
}) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const organizationId = useAuthStore((state) => state.selectedOrgId);
  const isSearchPending = search.trim() !== debouncedSearch;

  useEffect(() => {
    const nextSearch = search.trim();
    if (nextSearch === debouncedSearch) return;
    const timeout = setTimeout(() => {
      setDebouncedSearch(nextSearch);
      setPage(0);
    }, 350);
    return () => clearTimeout(timeout);
  }, [search, debouncedSearch]);

  const rolesQuery = useQuery({
    queryKey: ['api-key-roles', organizationId, page, debouncedSearch],
    queryFn: () => getApiKeyRoles(organizationId ?? '', page, debouncedSearch),
    placeholderData: keepPreviousData,
    enabled: !!organizationId,
    retry: false,
  });
  const rulesQuery = useQuery({
    queryKey: ['api-key-scope-rules', organizationId],
    queryFn: () => listApiKeyScopeRules(organizationId ?? ''),
    enabled: !!organizationId,
    retry: false,
  });
  const rules = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data]);
  const rulesBySlug = useMemo(
    () => new Map(rules.map((rule) => [rule.RoleSlug, rule])),
    [rules]
  );
  const displayRules = useMemo(
    () =>
      (rolesQuery.data?.items ?? []).flatMap((role): ApiKeyScopeRule[] => {
        const configuredRule = rulesBySlug.get(role.slug);
        if (configuredRule) return configuredRule.IsVisible ? [configuredRule] : [];
        const defaults = getRoleRuleDefaults(role);
        if (!defaults) return [];
        return [{
          ItemId: role.itemId,
          OrganizationId: organizationId ?? '',
          RoleSlug: role.slug,
          ...defaults,
          SortOrder: 0,
          IsVisible: true,
          IsEnabled: true,
          Includes: [],
          Requires: [],
        }];
      }),
    [organizationId, rolesQuery.data?.items, rulesBySlug]
  );
  const groups = groupApiKeyScopeRules(displayRules);
  const isFetching =
    rolesQuery.isFetching || rulesQuery.isFetching || rolesQuery.isPlaceholderData || isSearchPending;

  const toggle = (roleSlug: string, checked: boolean) => {
    const includedRoles = new Set<string>();
    const collectIncludedRoles = (value: string) => {
      const rule = rulesBySlug.get(value);
      for (const reference of rule?.Includes ?? []) {
        if (reference.Type !== 'role' || includedRoles.has(reference.Value)) continue;
        includedRoles.add(reference.Value);
        collectIncludedRoles(reference.Value);
      }
    };
    collectIncludedRoles(roleSlug);
    const explicitRoles = checked
      ? [
          ...selection.explicitRoles.filter((value) => !includedRoles.has(value)),
          ...(!selection.explicitRoles.includes(roleSlug) ? [roleSlug] : []),
        ]
      : selection.explicitRoles.filter((value) => value !== roleSlug);
    onChange(resolveApiKeyScopeGrant(rules, explicitRoles));
  };

  const renderRule = (rule: ApiKeyScopeRule, groupLabel: string, isAll = false) => {
    const implied = selection.impliedRoles.includes(rule.RoleSlug);
    return (
      <label key={rule.ItemId} className={isAll ? 'flex cursor-pointer items-center gap-2' : 'flex cursor-pointer items-start gap-2 rounded p-2 text-sm hover:bg-neutral-50'}>
        <input
          type="checkbox"
          disabled={isFetching || !rule.IsEnabled || implied}
          value={rule.RoleSlug}
          aria-label={`${groupLabel}: ${rule.Label}`}
          title={isAll ? `All ${groupLabel} scopes` : undefined}
          className={isAll ? 'shrink-0 accent-primary' : 'mt-1 shrink-0 accent-primary'}
          checked={selection.checkedRoles.includes(rule.RoleSlug)}
          onChange={(event) => toggle(rule.RoleSlug, event.target.checked)}
        />
        <span className="min-w-0 break-words">{isAll ? groupLabel : rule.Label}</span>
      </label>
    );
  };

  const totalPages = Math.max(1, Math.ceil((rolesQuery.data?.totalCount ?? 0) / ROLES_PAGE_SIZE));
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">Scopes</legend>
      <Input type="search" aria-label="Search scopes" placeholder="Search scopes" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }} />
      <div aria-busy={isFetching} className="h-[260px] overflow-y-auto rounded-md border border-neutral-200 p-3">
        {!organizationId ? (
          <p className="text-sm text-muted-foreground">Select an organization to load scopes.</p>
        ) : rolesQuery.isPending || rulesQuery.isPending ? (
          <p role="status" className="text-sm text-muted-foreground">Loading scopes…</p>
        ) : rolesQuery.isError || rulesQuery.isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>Couldn’t load scopes.</p>
            <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => { void rolesQuery.refetch(); void rulesQuery.refetch(); }}>Retry scopes</Button>
          </div>
        ) : !groups.length ? (
          <p className="text-sm text-muted-foreground">{search ? 'No scopes match your search.' : 'No scope roles available on this page.'}</p>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => (
              <fieldset key={group.key} aria-label={group.label}>
                <legend className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  {group.allRule ? renderRule(group.allRule, group.label, true) : group.label}
                </legend>
                <div className="grid grid-cols-2 gap-x-2 sm:grid-cols-3">{group.rules.map((rule) => renderRule(rule, group.label))}</div>
              </fieldset>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{selection.explicitRoles.length} selected</span>
        <nav aria-label="Scopes pagination" className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" disabled={page === 0 || isFetching} onClick={() => setPage((value) => value - 1)}>Previous</Button>
          <span aria-live="polite">Page {page + 1} of {totalPages}</span>
          <Button type="button" size="sm" variant="outline" disabled={isFetching || page + 1 >= totalPages} onClick={() => setPage((value) => value + 1)}>Next</Button>
        </nav>
      </div>
    </fieldset>
  );
}
